/**
 * Schema-driven helpery pro AdminMachines form.
 *
 * Po odříznutí hardcoded fallbacků drží jen čistě schema-driven logiku —
 * žádný per-process ``PROCESS_PARAM_KEYS`` / ``ProcessKind`` fallback. Zdrojem
 * pravdy je ``machine_config_schema`` (``x-form-section`` + ``default`` /
 * ``required`` metadata; seed migrace ``zzaa`` / ``zzau``).
 *
 * Helpery:
 * - ``processParamsKeysFromSchema(schema)`` — set klíčů s ``x-form-section:
 *   "process_params"``.
 * - ``defaultsFromSchema(schema)`` — ``{key: default}`` z ``properties[*].default``
 *   (deklarativní pre-fill při zakládání stroje; nahrazuje smazané
 *   ``defaultConstraints`` / ``defaultProcessParams``).
 * - ``fillMissingDefaults(schema, config)`` — doplní defaulty jen chybějícím
 *   klíčům (Rozjezd „Založit vlastní", úprava stroje; KAN-1158).
 */

import {
  appliesWhenSatisfied,
  type JsonSchema,
} from "@/components/admin/ModuleConfigForm";

export type MachineConstraints = Record<string, unknown>;

/**
 * Vyčte set klíčů s ``x-form-section: "process_params"`` z JSON Schema.
 * Malformed / prázdné schema → prázdný set.
 */
export function processParamsKeysFromSchema(
  schema: JsonSchema | null | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!schema || typeof schema !== "object") return out;
  const props = (schema as { properties?: Record<string, unknown> }).properties;
  if (!props || typeof props !== "object") return out;
  for (const [key, field] of Object.entries(props)) {
    if (
      field &&
      typeof field === "object" &&
      (field as Record<string, unknown>)["x-form-section"] === "process_params"
    ) {
      out.add(key);
    }
  }
  return out;
}

/**
 * Vyčte ``{key: default}`` z ``properties[*].default`` JSON Schématu —
 * deklarativní pre-fill machine configu při zakládání stroje (nahrazuje
 * smazané hardcoded ``defaultConstraints`` / ``defaultProcessParams``).
 * Malformed / prázdné schema → ``{}``.
 */
export function defaultsFromSchema(
  schema: JsonSchema | null | undefined,
): MachineConstraints {
  const out: MachineConstraints = {};
  if (!schema || typeof schema !== "object") return out;
  const props = (schema as { properties?: Record<string, unknown> }).properties;
  if (!props || typeof props !== "object") return out;
  for (const [key, field] of Object.entries(props)) {
    if (
      !field ||
      typeof field !== "object" ||
      !("default" in (field as Record<string, unknown>))
    ) {
      continue;
    }
    const value = (field as Record<string, unknown>).default;
    // ``"default": null`` znamená „výchozí hodnota NENÍ", ne „výchozí hodnota
    // je null" (KAN-1170). Zasít null do configu znamená, že založení stroje
    // spadne na validaci schématu — ``None is not of type 'number'``.
    //
    // ⚠️ Editor takové schéma NEVYROBÍ a tenhle strážce není záplata na něj:
    // obě serializace (``fieldsToSchema``, ``configuratorFieldsToSchema``)
    // null u defaultu zahodí a hlídá to regresní test. Ověřeno 2026-09-11 —
    // ani seed, ani migrace dnes ``"default": null`` nezapisují, takže se
    // tenhle stav v repozitáři zatím nevyskytuje.
    //
    // Drží se tu proto, že schéma NEMUSÍ vzniknout editorem: ručně sestavené
    // tělo ``PATCH …/schemas`` projde bez strážce (JSON Schema u ``default``
    // libovolnou hodnotu dovoluje), a chyba by pak padla až o dvě obrazovky
    // dál, při zakládání stroje. Čtecí strana je správné místo — spraví
    // i schéma, které tu null už uloženou má.
    //
    // Nula ani ``false`` se tím netýkají: to jsou hodnoty, ne nepřítomnost.
    if (value === null) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Doplní do ``config`` výchozí hodnoty klíčů, které v něm úplně CHYBÍ (KAN-1158).
 *
 * Existující hodnotu nikdy nepřepíše a nic neodebere — kromě právě doplněného
 * klíče, jehož ``applies_when`` nad výsledkem neplatí (stroj s kapacitou 1 by
 * jinak vezl „čas změny", na který se ho nikdo neptal).
 *
 * Proč to existuje: stroj, kterému k deklarovanému limitu chybí číslo, brána
 * odmítne. Zakládání strojem formuláře default zaselo odjakživa, ale „Založit
 * vlastní" v Rozjezdu a úprava stroje staršího než klíč ne — tudy stroje bez
 * čísla přibývaly. Nic se tím neukládá: zapíše to až člověk, který formulář
 * uloží.
 *
 * Nic k doplnění → vrací TENTÝŽ objekt, ať volání uvnitř ``setState`` nic
 * nepřekresluje.
 */
export function fillMissingDefaults(
  schema: JsonSchema | null | undefined,
  config: MachineConstraints,
): MachineConstraints {
  const props = schema?.properties ?? {};
  const defaults = defaultsFromSchema(schema);
  const added = Object.keys(defaults).filter((key) => config[key] == null);
  if (added.length === 0) return config;
  const next: MachineConstraints = { ...config };
  for (const key of added) next[key] = defaults[key];
  for (const key of added) {
    const prop = props[key];
    if (prop && !appliesWhenSatisfied(prop, next)) delete next[key];
  }
  return next;
}
